import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Printer, Users, UserCheck, UserX } from "lucide-react";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../../common/Toast/Toast";
import DataTable from "../../common/DataTable/DataTable";
import PrintRequestModal from "../../common/PrintRequestModal/PrintRequestModal";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import useNavigationStore from "../../store/navigationStore";
import { useTranslation } from "react-i18next";
import { formatTableColumnLabel } from "../../utils/tableUtils/formatTableColumnLabel";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import "./PrintRequest.css";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const EXCLUDED_COLUMNS = ["credential_id"];
const ITEMS_PER_PAGE = 8;

const STATUS_CONFIG = {
  Printed: {
    dotColor: "var(--issued-credential-template-card-status-done-bg)",
    bgColor: "var(--cf-active-bg-color)",
    textColor: "var(--cf-active-text-color)",
    label: "workspace:printed",
  },
  "Not Printed": {
    dotColor: "var(--cf-warning-border-color)",
    bgColor: "var(--email-warning-container-bg)",
    textColor: "var(--cf-warning-text-color)",
    label: "workspace:not_printed",
  },
};

export default function PrintRequest() {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [tableData, setTableData] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [tableLoading, setTableLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printModalMode, setPrintModalMode] = useState("all");
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [certificateName, setCertificateName] = useState("");

  const { legacyTransactionId: paramLegacyTransactionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );

  const totalRecipients = kpiData?.total_recipients ?? tableData.count ?? 0;
  const isPartialSelection =
    selectedRows.length > 0 && selectedRows.length < totalRecipients;

  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const legacyTransactionId =
    paramLegacyTransactionId || localStorage.getItem("legacyTransactionId");
  const credentialTemplateId = localStorage.getItem("credentialTemplateId");

  useEffect(() => {
    if (
      !currentProgramme ||
      !currentBatch ||
      !legacyTransactionId ||
      !credentialTemplateId
    ) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [
    currentProgramme,
    currentBatch,
    legacyTransactionId,
    credentialTemplateId,
    navigate,
  ]);

  useEffect(() => {
    if (!legacyTransactionId) return;

    const fetchCertificateName = async () => {
      try {
        const res = await GET(
          `${EntityConstants.GET_USER_PREVIEW_SVG}?issuer_credential_template_id=${legacyTransactionId}`,
        );
        const data = await res.json();
        setCertificateName(data.certificate_name || "");
      } catch (error) {
        console.debug("Failed to fetch certificate name:", error);
      }
    };

    fetchCertificateName();
  }, [legacyTransactionId]);

  const fetchKpiStats = useCallback(async () => {
    if (!legacyTransactionId) return;
    setKpiLoading(true);
    try {
      const res = await GET(
        `${EntityConstants.GET_PRINT_STATS}/${legacyTransactionId}`,
      );
      const data = await res.json();
      setKpiData(data);
    } catch (err) {
      console.error("Failed to fetch print stats:", err);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_to_fetch_print_statistics"),
      });
    } finally {
      setKpiLoading(false);
    }
  }, [legacyTransactionId, t]);

  const fetchTableData = useCallback(
    async (search = "", currentOffset = 0, signal) => {
      if (!legacyTransactionId) return;
      setTableLoading(true);
      try {
        let url = `${EntityConstants.GET_PRINT_DETAILS}/${legacyTransactionId}?limit=${ITEMS_PER_PAGE}&offset=${currentOffset}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;

        const res = await GET(url, { signal });
        const data = await res.json();
        setTableData(data);

        if (data.results && data.results.length > 0) {
          const keys = Object.keys(data.results[0]).filter(
            (k) => !EXCLUDED_COLUMNS.includes(k),
          );
          setColumns(
            keys.map((key) => ({
              key,
              label: formatTableColumnLabel(key),
            })),
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Failed to fetch print details:", err);
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_to_fetch_print_details"),
        });
      } finally {
        setTableLoading(false);
      }
    },
    [legacyTransactionId, t],
  );

  useEffect(() => {
    fetchKpiStats();
  }, [fetchKpiStats]);

  const searchFn = useCallback(
    async (term, signal) => {
      setAppliedSearchTerm(term);
      await fetchTableData(term, 0, signal);
    },
    [fetchTableData],
  );

  const { handleSearch, cleanup: cleanupSearch } = useSearchDebounce(
    searchFn,
    400,
  );

  useEffect(() => {
    fetchTableData(appliedSearchTerm, offset);
  }, [offset, appliedSearchTerm, fetchTableData]);

  useEffect(() => {
    return () => cleanupSearch();
  }, [cleanupSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length === 0) {
      setAppliedSearchTerm("");
      setOffset(0);
      return;
    }
    setOffset(0);
    handleSearch(value, { minChars: 1 });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    cleanupSearch();
    setAppliedSearchTerm(searchTerm);
    setOffset(0);
  };

  const handleNext = () => {
    if (tableData.next) setOffset((prev) => prev + ITEMS_PER_PAGE);
  };

  const handlePrevious = () => {
    if (tableData.previous)
      setOffset((prev) => Math.max(0, prev - ITEMS_PER_PAGE));
  };

  const handlePageClick = (page) => {
    setOffset((page - 1) * ITEMS_PER_PAGE);
  };

  const handleRowSelect = (rowId, isSelected) => {
    setSelectedRows((prev) =>
      isSelected ? [...prev, rowId] : prev.filter((id) => id !== rowId),
    );
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const currentPageIds = tableData.results.map((row) => row.credential_id);
      setSelectedRows((prev) => {
        const combined = new Set([...prev, ...currentPageIds]);
        return [...combined];
      });
    } else {
      const currentPageIds = new Set(
        tableData.results.map((row) => row.credential_id),
      );
      setSelectedRows((prev) => prev.filter((id) => !currentPageIds.has(id)));
    }
  };

  const handleOpenPrintAllModal = () => {
    setPrintModalMode("all");
    setShowPrintModal(true);
  };

  const handleOpenPrintSelectedModal = () => {
    if (selectedRows.length === 0) {
      Toast({
        type: "warning",
        title: t("workspace:no_selection"),
        message: t("workspace:select_at_least_one_recipient_to_print"),
      });
      return;
    }
    setPrintModalMode("selected");
    setShowPrintModal(true);
  };

  const handleCreatePrintRequest = async () => {
    setIsPrintLoading(true);
    try {
      let payload;
      if (printModalMode === "selected") {
        payload = { credential_ids: selectedRows };
      } else {
        payload = { issuer_credential_template_id: legacyTransactionId };
      }

      const response = await POST(
        EntityConstants.POST_REQUEST_CERTIFICATE,
        payload,
      );

      if (!response.ok) {
        throw new Error("Failed to create print request");
      }

      const data = await response.json();

      Toast({
        type: "success",
        title: t("workspace:success"),
        message:
          data.message || t("workspace:print_request_created_successfully"),
      });

      setShowPrintModal(false);
      setSelectedRows([]);

      navigate(`/issuer/credentials/${legacyTransactionId}/print`);
    } catch (error) {
      console.error("Error creating print request:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_create_print_request"),
      });
    } finally {
      setIsPrintLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(`/issuer/credentials/${legacyTransactionId}/print`);
  };

  const handleWorkspaceClick = () => navigate("/issuer/workspace");

  const handleProgrammeClick = () =>
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);

  const handleBatchClick = () =>
    navigate("/issuer/workspace/batches/credentials");

  const handlePendingIssuanceClick = () => {
    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${legacyTransactionId}`,
    );
  };

  const handlePrintClick = () =>
    navigate(`/issuer/credentials/${legacyTransactionId}/print`);

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback(e);
    }
  };

  const modalRecipientCount =
    printModalMode === "selected"
      ? selectedRows.length
      : (kpiData?.total_recipients ?? 0);

  return (
    <div
      className="min-h-screen mt-20 bg-cfSecondary w-full"
      id={pageRootTestIds.printRequest}
    >
      <div className="w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center min-h-[4rem] justify-between w-full py-2">
          <div className="min-w-0 w-full">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-500 truncate min-w-0 max-w-full">
                  <button
                    id="print-request-breadcrumb-workspace"
                    onClick={handleWorkspaceClick}
                    onKeyDown={(e) => handleKeyDown(e, handleWorkspaceClick)}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {t("workspace:workspace")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-request-breadcrumb-programme"
                    onClick={handleProgrammeClick}
                    onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={
                      currentProgramme?.programme_name ||
                      t("workspace:programme")
                    }
                  >
                    {currentProgramme?.programme_name ||
                      t("workspace:programme")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-request-breadcrumb-batch"
                    onClick={handleBatchClick}
                    onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={currentBatch?.batch_name || t("workspace:batch")}
                  >
                    {currentBatch?.batch_name || t("workspace:batch")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-request-breadcrumb-certificate"
                    onClick={handlePendingIssuanceClick}
                    onKeyDown={(e) =>
                      handleKeyDown(e, handlePendingIssuanceClick)
                    }
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer breadcrumb-item"
                    title={certificateName || t("workspace:loading")}
                  >
                    {certificateName ? certificateName : t("workspace:loading")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="print-request-breadcrumb-print-pdf"
                    onClick={handlePrintClick}
                    onKeyDown={(e) => handleKeyDown(e, handlePrintClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer"
                  >
                    {t("workspace:pdf_print")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span className="text-gray-800 font-medium">
                    {t("workspace:print_request")}
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between my-4 px-4">
        <div className="flex gap-2 justify-start">
          <div
            id="print-request-back"
            onClick={handleBackClick}
            onKeyDown={(e) => handleKeyDown(e, handleBackClick)}
            role="button"
            tabIndex={0}
            className="mt-[0.2rem] cursor-pointer"
          >
            <img src={ArrowLeftIcon} alt="Back" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {t("workspace:new_print_request")}
            </h1>
            <p className="text-gray-500">
              {t("workspace:select_recipients_create_print_request")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mx-4 mb-6">
        <div className="flex items-center gap-3.5 py-[1.8rem] px-6 bg-white border-[1.5px] border-gray-200 rounded-xl transition-all duration-200 hover:shadow-md">
          <div className="bg-[#eef4ff] text-cfPrimary flex items-center justify-center w-12 h-12 rounded-[10px] flex-shrink-0">
            <Users size={24} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500 mb-0.5">
              {t("workspace:total_recipients")}
            </span>
            {kpiLoading ? (
              <div className="kpi-skeleton" />
            ) : (
              <span
                id="print-request-total-recipients"
                className="text-3xl font-bold text-gray-900 leading-tight"
              >
                {kpiData?.total_recipients ?? 0}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3.5 py-[1.8rem] px-6 bg-white border-[1.5px] border-gray-200 rounded-xl transition-all duration-200 hover:shadow-md">
          <div className="bg-[#ecfdf3] text-[#12b76a] flex items-center justify-center w-12 h-12 rounded-[10px] flex-shrink-0">
            <UserCheck size={24} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500 mb-0.5">
              {t("workspace:printed")}
            </span>
            {kpiLoading ? (
              <div className="kpi-skeleton" />
            ) : (
              <span
                id="print-request-printed-count"
                className="text-3xl font-bold text-gray-900 leading-tight"
              >
                {kpiData?.printed_recipients ?? 0}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3.5 py-[1.8rem] px-6 bg-white border-[1.5px] border-gray-200 rounded-xl transition-all duration-200 hover:shadow-md">
          <div className="bg-[#fff6ed] text-[#f79009] flex items-center justify-center w-12 h-12 rounded-[10px] flex-shrink-0">
            <UserX size={24} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500 mb-0.5">
              {t("workspace:not_printed")}
            </span>
            {kpiLoading ? (
              <div className="kpi-skeleton" />
            ) : (
              <span
                id="print-request-not-printed-count"
                className="text-3xl font-bold text-gray-900 leading-tight"
              >
                {kpiData?.not_printed_recipients ?? 0}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mx-4 mb-4 gap-3 flex-col md:flex-row">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <form
            onSubmit={handleSearchSubmit}
            className="bg-cfSecondary p-2 rounded-lg w-full md:w-[25rem] self-center md:self-end md:m-0 border border-gray-400"
          >
            <input
              type="text"
              name="search"
              id="print-request-input-search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t("workspace:search_by_name_email_roll")}
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>

        <div className="flex flex-col md:flex-row w-full justify-start md:justify-end items-stretch md:items-start gap-2.5">
          <button
            id="print-request-submit-print"
            className="bg-cfPrimary hover:bg-cfPrimaryDark flex items-center justify-center gap-2 py-2.5 px-4 text-white text-sm font-semibold rounded-md cursor-pointer transition-colors duration-150 whitespace-nowrap border-none"
            onClick={
              isPartialSelection
                ? handleOpenPrintSelectedModal
                : handleOpenPrintAllModal
            }
          >
            <Printer size={18} />
            {isPartialSelection
              ? `${t("workspace:print")} ${selectedRows.length} ${
                  selectedRows.length === 1
                    ? t("workspace:certificate")
                    : t("workspace:certificates")
                }`
              : `${t("workspace:print_all")}`}
          </button>
        </div>
      </div>

      <div className="mx-4 pb-8">
        <DataTable
          columns={columns}
          data={tableData.results}
          loading={tableLoading}
          emptyMessage={t("workspace:no_result_found")}
          selectable={true}
          selectedRows={selectedRows}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          rowKey="credential_id"
          dataTestId="print-request-table"
          statusConfig={STATUS_CONFIG}
          totalCount={tableData.count}
          currentOffset={offset}
          limit={ITEMS_PER_PAGE}
          hasNext={!!tableData.next}
          hasPrevious={!!tableData.previous}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onPageClick={handlePageClick}
        />
      </div>

      <PrintRequestModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        totalRecipients={modalRecipientCount}
        isLoading={isPrintLoading}
        onCreatePrintRequest={handleCreatePrintRequest}
      />
    </div>
  );
}
